import { createAsyncThunk } from '@reduxjs/toolkit';
import ApiService from '../../global/services/api.service';
import { ListResponseDTO, MatchingMethod } from '../../global';
import {
  MonitoringTransportDTO,
  MonitoringGeneralInfo,
  MonitoringGeneralRequestFilter,
  MonitoringSearchOption,
  MonitoringTeam,
  MonitoringTransportSearchParams,
  MonitoringVehicleDetailedDTO,
} from '../types';
import { AppDispatch, RootState } from '../../layout/store';
import QueryBuilder from '../../shared/utils/query-builder';
import LocationService from '../../services/location.service';

export const getMonitoringTeams = createAsyncThunk(
  'monitoring/getMonitoringTeams',
  async () => {
    const { data } =
      await ApiService.get<ListResponseDTO<MonitoringTeam>>(
        '/monitoring/teams',
      );
    return (data?.items || []).map((item: MonitoringTeam) => ({
      id: item.id,
      name: item.name,
      total: item.transportsCount,
      drivers: [],
    }));
  },
);

export const getMonitoringTransports = createAsyncThunk(
  'monitoring/getMonitoringTransports',
  async (id: number) => {
    const { data } = await ApiService.post<
      ListResponseDTO<MonitoringTransportDTO>
    >('/monitoring/transports/', {
      query: [
        {
          filters: [
            {
              field: 'team:id',
              value: String(id),
              matchingMethod: MatchingMethod.EXACT,
              values: null,
            },
          ],
        },
      ],
    });
    return { transports: data?.items || [], teamId: id };
  },
);
export const getMonitoringLocations = createAsyncThunk(
  'monitoring/getMonitoringLocations',
  async (search: string) => {
    const { data } = await LocationService.getLocations({ search });
    return data;
  },
);

export const getMonitoringTransportsByTerm = createAsyncThunk(
  'monitoring/getMonitoringDriversByTerm',
  async (data: MonitoringTransportSearchParams) => {
    const { search, team } = data;
    if (!data) {
      return { drivers: [], total: 0 };
    }
    const body = new QueryBuilder()
      .addOffset(0)
      .addLimit(100)
      .or({
        field: MonitoringSearchOption.LICENSE_PLATE,
        value: search,
        matchingMethod: MatchingMethod.CONTAINS,
      })
      .or({
        field: MonitoringSearchOption.DRIVER_NAME,
        value: search,
        matchingMethod: MatchingMethod.CONTAINS,
      })
      .and({
        field: MonitoringSearchOption.TEAM_ID,
        values: team,
        matchingMethod: MatchingMethod.CONTAINS,
      });

    const { data: transportsData } = await ApiService.post<
      ListResponseDTO<MonitoringTransportDTO>
    >('/monitoring/transports', body);
    return {
      drivers: transportsData?.items,
      total: transportsData?.totalItems,
    };
  },
);

export const getMonitoringGeneralInfo = createAsyncThunk<
  null | MonitoringGeneralInfo,
  MonitoringGeneralRequestFilter,
  {
    state: RootState;
    dispatch: AppDispatch;
    signal: AbortSignal;
  }
>(
  'monitoring/getMonitoringGeneralInfo',
  async (filter: MonitoringGeneralRequestFilter, thunkAPI) => {
    const response = await ApiService.get<MonitoringGeneralInfo>(
      `/monitoring/transport/general/${filter.transportId}?startTime=${filter.startDate}&endTime=${filter.endDate}`,
      {},
      { cache: true, signal: thunkAPI.signal },
    );
    if (response.ok) {
      return response.data;
    }
    return null;
  },
);

class MonitoringService {
  static ENDPOINT = '/monitoring';

  static getMonitoringVehicleById(id: number) {
    return ApiService.get<MonitoringVehicleDetailedDTO>(
      `${MonitoringService.ENDPOINT}/transports/${id}`,
    );
  }
}

export default MonitoringService;
